import type { CobrancaAcaoEtapa, CobrancaAcaoCanal, CobrancaAcaoStatus, TipoLembreteAutomatico } from '../../../types';

/**
 * Fonte única da régua de cobrança na interface. Se a régua mudar, muda aqui —
 * a tela, os filtros e os textos de ajuda leem tudo deste arquivo.
 *
 * Régua em teste desde a reunião de 23/10/2026 (Meryene):
 *   D-3, D0, D+3   → automáticos (WhatsApp via UaizyChat); D-1 até o D0 entrar
 *   D+7            → automático só se ninguém registrou contato depois do vencimento
 * Régua de 11/08/2026:
 *   D-1, D+3, D+7  → automáticos (WhatsApp via UaizyChat)
 *   D+10, D+17, D+24 → contato humano, registrado em cobranca_acao_manual
 *   D+4 → primeiro contato humano, desde 02/10/2026 (#6145)
 */

export interface EtapaAutomaticaMeta {
    tipo: TipoLembreteAutomatico;
    label: string;
    descricao: string;
    /** Etapa aposentada — só aparece porque existe histórico dela. */
    legado?: boolean;
}

export const ETAPAS_AUTOMATICAS: EtapaAutomaticaMeta[] = [
    { tipo: 'D-3', label: 'D-3', descricao: '3 dias antes do vencimento' },
    { tipo: 'D0', label: 'D0', descricao: 'No dia do vencimento' },
    { tipo: 'D-1', label: 'D-1', descricao: 'Véspera do vencimento — sai enquanto o D0 não entra no ar' },
    { tipo: 'D+3', label: 'D+3', descricao: '3 dias após o vencimento' },
    { tipo: 'D+7', label: 'D+7', descricao: '7 dias após — não sai se já houve contato humano' },
    { tipo: 'D+10', label: 'D+10 (legado)', descricao: 'Aposentado em 11/08/2026 — virou contato humano', legado: true },
];

export interface EtapaManualMeta {
    etapa: CobrancaAcaoEtapa;
    titulo: string;
    /** O que a pessoa tem que fazer — vira o texto de ajuda do card. */
    instrucao: string;
    /** Rótulo do botão principal. */
    acaoLabel: string;
    badge: string;
    accent: string;
}

export const ETAPAS_MANUAIS: Record<CobrancaAcaoEtapa, EtapaManualMeta> = {
    'D+4': {
        etapa: 'D+4',
        titulo: 'Primeiro contato humano',
        instrucao: 'Ligue ou mande mensagem lembrando do boleto em atraso e oferecendo a segunda via.',
        acaoLabel: 'Registrar contato',
        badge: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
        accent: 'border-l-yellow-400',
    },
    'D+10': {
        etapa: 'D+10',
        titulo: 'Contato humano',
        instrucao: 'Ligue ou mande mensagem informando o atraso e o risco de negativação.',
        acaoLabel: 'Registrar contato',
        badge: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
        accent: 'border-l-amber-400',
    },
    'D+17': {
        etapa: 'D+17',
        titulo: 'Notificação extrajudicial',
        instrucao: 'Gere a notificação extrajudicial e registre o envio ao cliente — é o envio que inicia o prazo de 5 dias úteis.',
        acaoLabel: 'Gerar notificação',
        badge: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300',
        accent: 'border-l-orange-500',
    },
    'D+24': {
        etapa: 'D+24',
        titulo: 'Conferência e negativação',
        instrucao: 'Confira se o pagamento entrou. Se não entrou, negative o consumidor no SPC.',
        acaoLabel: 'Registrar conferência',
        badge: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
        accent: 'border-l-red-500',
    },
};

export const ORDEM_ETAPAS_MANUAIS: CobrancaAcaoEtapa[] = ['D+4', 'D+10', 'D+17', 'D+24'];

export const CANAIS: { value: CobrancaAcaoCanal; label: string }[] = [
    { value: 'whatsapp', label: 'WhatsApp' },
    { value: 'ligacao', label: 'Ligação' },
    { value: 'email', label: 'E-mail' },
    { value: 'presencial', label: 'Presencial' },
    { value: 'outro', label: 'Outro' },
];

/** Desfecho de um contato — mesma lista do "Registrar contato" do Gerenciar Cobrança. */
export const RESULTADOS_CONTATO: { value: string; label: string }[] = [
    { value: 'nao_atendeu', label: 'Não atendeu' },
    { value: 'sem_retorno', label: 'Sem retorno' },
    { value: 'atendeu', label: 'Atendeu' },
    { value: 'prometeu_pagar', label: 'Prometeu pagar' },
    { value: 'recusou_negociar', label: 'Recusou negociar' },
    { value: 'contato_invalido', label: 'Contato inválido' },
    { value: 'outro', label: 'Outro' },
];

export const STATUS_ACAO_META: Record<CobrancaAcaoStatus, { label: string; classes: string }> = {
    pendente: { label: 'Pendente', classes: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300' },
    concluido: { label: 'Concluído', classes: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300' },
    dispensado: { label: 'Dispensado', classes: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400' },
};

/** Departamentos que podem cuidar dos contatos humanos. */
export const DEPARTAMENTOS_COBRANCA = ['Financeiro', 'Pós Venda'];

export const formatBRL = (v: number | null | undefined): string =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(Number(v ?? 0));

/** Datas do banco vêm como 'YYYY-MM-DD'; meio-dia evita voltar um dia no fuso. */
export const formatDia = (iso?: string | null): string =>
    iso ? new Date(`${iso.slice(0, 10)}T12:00:00`).toLocaleDateString('pt-BR') : '—';

export const diasDesde = (iso?: string | null): number => {
    if (!iso) return 0;
    const alvo = new Date(`${iso.slice(0, 10)}T12:00:00`);
    const hoje = new Date();
    hoje.setHours(12, 0, 0, 0);
    return Math.round((hoje.getTime() - alvo.getTime()) / 86_400_000);
};
